/**
 * Feature and plan codes mirrored from backend/src/shared/billing/planCatalog.ts.
 * The session payload is the source of which features are on; this file maps UI paths.
 */

export const PLAN_IDS = ['free', 'basic', 'professional', 'enterprise'] as const;

export type PlanId = (typeof PLAN_IDS)[number];

export const PLAN_FEATURES = [
  'overview',
  'patients',
  'appointments',
  'catalog',
  'payments',
  'leads',
  'finance',
  'promotions',
  'internal_chat',
  'messaging',
  'warehouse',
  'labo',
  'hr',
  'reports',
  'automation',
] as const;

export type PlanFeature = (typeof PLAN_FEATURES)[number];

const FREE_FEATURES = [
  'overview',
  'patients',
  'appointments',
  'catalog',
  'payments',
] as const satisfies readonly PlanFeature[];

const BASIC_FEATURES = [
  ...FREE_FEATURES,
  'leads',
  'finance',
  'promotions',
  'internal_chat',
] as const satisfies readonly PlanFeature[];

const PROFESSIONAL_FEATURES = [
  ...BASIC_FEATURES,
  'messaging',
  'warehouse',
  'labo',
  'hr',
  'reports',
  'automation',
] as const satisfies readonly PlanFeature[];

/** Display + dialog defaults. Enforcement uses the session payload from the API. */
export const PLAN_LIMITS: Record<PlanId, { maxUsers: number | null; maxClinics: number | null }> = {
  free: { maxUsers: 3, maxClinics: 1 },
  basic: { maxUsers: 10, maxClinics: 1 },
  professional: { maxUsers: 50, maxClinics: 10 },
  enterprise: { maxUsers: null, maxClinics: null },
};

export const PLAN_FEATURE_SETS: Record<PlanId, readonly PlanFeature[]> = {
  free: FREE_FEATURES,
  basic: BASIC_FEATURES,
  professional: PROFESSIONAL_FEATURES,
  enterprise: PROFESSIONAL_FEATURES,
};

export interface OrgEntitlements {
  plan: PlanId;
  features: PlanFeature[];
  maxUsers: number | null;
  maxClinics: number | null;
}

const isPlanFeature = (value: string): value is PlanFeature =>
  (PLAN_FEATURES as readonly string[]).includes(value);

/** Longest prefix wins. Null means the path is available on every plan. */
const PATH_FEATURES: ReadonlyArray<readonly [string, PlanFeature]> = [
  ['/patients', 'patients'],
  ['/customer-sources', 'patients'],
  ['/appointments', 'appointments'],
  ['/appointment-types', 'appointments'],
  ['/treatment-catalog', 'catalog'],
  ['/treatment-categories', 'catalog'],
  ['/treatment-uoms', 'catalog'],
  ['/treatment-journals', 'catalog'],
  ['/treatment-plans', 'catalog'],
  ['/treatment-sessions', 'catalog'],
  ['/treatments', 'catalog'],
  ['/drugs', 'catalog'],
  ['/prescription-templates', 'catalog'],
  ['/prescriptions', 'catalog'],
  ['/diagnosis-codes', 'catalog'],
  ['/diagnosis-blocks', 'catalog'],
  ['/payments', 'payments'],
  ['/payment-methods', 'payments'],
  ['/leads', 'leads'],
  ['/incomes', 'finance'],
  ['/income-categories', 'finance'],
  ['/expenses', 'finance'],
  ['/expense-categories', 'finance'],
  ['/cashbook', 'finance'],
  ['/marketing/promotions', 'promotions'],
  ['/marketing/referrers', 'reports'],
  ['/marketing/referral-commissions', 'reports'],
  ['/promotions', 'promotions'],
  ['/internal-chat', 'internal_chat'],
  ['/messaging', 'messaging'],
  ['/outbound', 'messaging'],
  ['/inventory', 'warehouse'],
  ['/labo', 'labo'],
  ['/attendance', 'hr'],
  ['/overtime', 'hr'],
  ['/work-timesheet', 'hr'],
  ['/kpi', 'hr'],
  ['/scheduling', 'hr'],
  ['/payroll', 'hr'],
  ['/commissions', 'hr'],
  ['/commission-rules', 'hr'],
  ['/reports', 'reports'],
  ['/automation', 'automation'],
  ['/dashboard', 'overview'],
];

export const featureForPath = (pathname: string): PlanFeature | null => {
  const path = pathname.split('?')[0] || '/';
  if (/^\/patients\/[^/]+\/lab-orders(\/|$)/.test(path)) return 'labo';

  let match: PlanFeature | null = null;
  let length = -1;
  for (const [prefix, feature] of PATH_FEATURES) {
    if (path === prefix || path.startsWith(`${prefix}/`)) {
      if (prefix.length > length) {
        length = prefix.length;
        match = feature;
      }
    }
  }
  return match;
};

export const hasPlanFeature = (
  entitlements: { features?: readonly string[] } | null | undefined,
  feature: PlanFeature
): boolean => {
  if (!entitlements?.features) return true;
  return entitlements.features.some((item) => isPlanFeature(item) && item === feature);
};
